import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  Building2, 
  Users, 
  FileCheck, 
  TrendingUp, 
  Shield, 
  Globe, 
  ArrowRight,
  CheckCircle2,
  BarChart3,
  Zap,
  ChevronRight,
  Play,
  Sparkles,
  Brain,
  Lock,
  ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { seedDemoData } from '@/services/api';

const LOGO_URL = "/admixio-logo.png";

// Demo Modal - Cinematic (stays dark overlay)
const DemoModal = ({ onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);

  const slides = [
    { tag: "Welcome to", title: "Admixio", subtitle: "The future of international student recruitment", stats: null, gradient: "from-blue-500 to-blue-700", icon: null, showLogo: true },
    { tag: "Global Network", title: "100+ Institutions", subtitle: "Partner universities and colleges across Canada, UK, USA & Australia", stats: [{ label: "Canada", value: "28" }, { label: "UK", value: "25" }, { label: "USA", value: "25" }, { label: "Australia", value: "22" }], gradient: "from-blue-600 to-indigo-600", icon: <Globe className="w-12 h-12" />, showLogo: false },
    { tag: "Explore Programs", title: "1,300+ Courses", subtitle: "Diplomas, Bachelors, Masters & PhDs with complete fee structures, intakes & eligibility", stats: [{ label: "Engineering", value: "400+" }, { label: "Business", value: "350+" }, { label: "Health", value: "200+" }, { label: "Arts & Science", value: "350+" }], gradient: "from-blue-500 to-emerald-500", icon: <GraduationCap className="w-12 h-12" />, showLogo: false },
    { tag: "Smart Matching", title: "AI Eligibility Checker", subtitle: "Instantly match students with programs based on GPA, IELTS, budget & preferences", stats: null, gradient: "from-amber-500 to-orange-500", icon: <Brain className="w-12 h-12" />, showLogo: false },
    { tag: "Get Started", title: "Join Admixio Today", subtitle: "Where Every Student Finds Their Future", stats: null, gradient: "from-blue-500 to-blue-700", icon: null, showLogo: true, isCTA: true },
  ];

  const SLIDE_DURATION = 4000;

  useEffect(() => {
    const progressInterval = setInterval(() => { setProgress(prev => prev >= 100 ? 100 : prev + (100 / (SLIDE_DURATION / 50))); }, 50);
    const slideTimer = setInterval(() => {
      setCurrentSlide(prev => {
        if (prev >= slides.length - 1) { clearInterval(slideTimer); clearInterval(progressInterval); return prev; }
        setProgress(0); return prev + 1;
      });
    }, SLIDE_DURATION);
    return () => { clearInterval(slideTimer); clearInterval(progressInterval); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const slide = slides[currentSlide];

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900 flex flex-col" data-testid="demo-modal">
      <button onClick={onClose} className="absolute top-6 right-6 z-50 text-white/60 hover:text-white transition-colors text-sm flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full" data-testid="close-demo-btn">
        Skip <span className="text-xs opacity-60">ESC</span>
      </button>
      <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} opacity-10 transition-all duration-1000`} style={{ filter: 'blur(100px)' }} />
      <div className="flex-1 flex items-center justify-center relative z-10 px-6">
        <div className="text-center max-w-3xl mx-auto">
          <div className="mb-8 flex justify-center" key={`icon-${currentSlide}`} style={{ animation: 'fadeSlideUp 0.8s ease-out forwards' }}>
            {slide.showLogo ? <img src={LOGO_URL} alt="Admixio" className="h-24 md:h-32" /> : slide.icon ? <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${slide.gradient} bg-opacity-20 flex items-center justify-center text-white`}>{slide.icon}</div> : null}
          </div>
          <p className="text-sm uppercase tracking-[0.3em] text-blue-400 mb-4 font-medium" key={`tag-${currentSlide}`} style={{ animation: 'fadeSlideUp 0.6s 0.1s ease-out both' }}>{slide.tag}</p>
          <h1 className={`text-4xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r ${slide.gradient} bg-clip-text text-transparent`} key={`title-${currentSlide}`} style={{ fontFamily: 'Outfit, sans-serif', animation: 'fadeSlideUp 0.8s 0.2s ease-out both' }}>{slide.title}</h1>
          <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-xl mx-auto leading-relaxed" key={`sub-${currentSlide}`} style={{ animation: 'fadeSlideUp 0.8s 0.3s ease-out both' }}>{slide.subtitle}</p>
          {slide.stats && (
            <div className="flex justify-center gap-6 md:gap-10" key={`stats-${currentSlide}`} style={{ animation: 'fadeSlideUp 0.8s 0.5s ease-out both' }}>
              {slide.stats.map((stat, i) => (
                <div key={`stat-${stat.label}`} className="text-center" style={{ animation: `fadeSlideUp 0.6s ${0.5 + i * 0.1}s ease-out both` }}>
                  <p className="text-2xl md:text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
          {slide.isCTA && (
            <div style={{ animation: 'fadeSlideUp 0.8s 0.5s ease-out both' }}>
              <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 px-10 text-lg inline-flex items-center gap-2 transition-colors" data-testid="demo-cta-btn">
                Open Portal <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="relative z-10 pb-8 px-6">
        <div className="max-w-xl mx-auto">
          <div className="flex justify-center gap-2 mb-4">
            {slides.map((_, i) => (
              <button key={`dot-${i}`} onClick={() => { setCurrentSlide(i); setProgress(0); }}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === currentSlide ? 'w-8 bg-blue-500' : i < currentSlide ? 'w-4 bg-blue-500/40' : 'w-4 bg-white/15'}`}
                data-testid={`demo-dot-${i}`} />
            ))}
          </div>
          <div className="h-0.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-blue-700 rounded-full transition-all duration-100 ease-linear" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
      <style>{`@keyframes fadeSlideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

// Animated Counter
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let start = 0; const step = end / (duration / 16);
        const timer = setInterval(() => { start += step; if (start >= end) { setCount(end); clearInterval(timer); } else { setCount(Math.floor(start)); } }, 16);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};

// Logo Intro Animation (stays dark)
const LogoIntro = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 500),
      setTimeout(() => setPhase(2), 1500),
      setTimeout(() => setPhase(3), 3000),
      setTimeout(() => setPhase(4), 4500),
      setTimeout(() => onComplete(), 5500),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  return (
    <div className={`fixed inset-0 z-50 bg-slate-900 flex items-center justify-center transition-opacity duration-1000 ${phase >= 4 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
      <div className="absolute inset-0 overflow-hidden">
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-blue-500/10 transition-all duration-1000 ${phase >= 1 ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`} style={{ filter: 'blur(120px)' }} />
      </div>
      <div className="relative z-10 flex flex-col items-center">
        <div className={`transition-all duration-1000 ${phase >= 2 ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
          <img src={LOGO_URL} alt="Admixio" className="h-24 md:h-28" />
        </div>
        <div className={`mt-6 text-center transition-all duration-1000 delay-500 ${phase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-lg md:text-xl text-slate-400 tracking-widest">Transforming Dreams into Degrees</p>
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [showIntro, setShowIntro] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => { seedDemoData().catch(() => {}); }, []);
  useEffect(() => { if (!showIntro) setTimeout(() => setIsVisible(true), 100); }, [showIntro]);
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setShowDemo(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const features = [
    { icon: <FileCheck className="w-6 h-6" strokeWidth={1.5} />, title: "Smart Application Tracking", description: "Real-time visibility into every application stage from submission to enrollment." },
    { icon: <Shield className="w-6 h-6" strokeWidth={1.5} />, title: "Secure Document Vault", description: "Bank-grade encrypted storage for sensitive student documents and credentials." },
    { icon: <Brain className="w-6 h-6" strokeWidth={1.5} />, title: "AI Eligibility Checker", description: "Instant eligibility assessment powered by advanced AI algorithms." },
    { icon: <BarChart3 className="w-6 h-6" strokeWidth={1.5} />, title: "Commission Management", description: "Transparent ledger for tracking payouts, incentives, and processing fees." },
    { icon: <Globe className="w-6 h-6" strokeWidth={1.5} />, title: "Global University Network", description: "Access to 500+ partner institutions across 25+ countries worldwide." },
    { icon: <TrendingUp className="w-6 h-6" strokeWidth={1.5} />, title: "Market Insights", description: "Data-driven analytics on trending courses and visa success rates." },
  ];

  const stats = [
    { value: 500, suffix: '+', label: 'Partner Universities', icon: <Building2 className="w-5 h-5" /> },
    { value: 50000, suffix: '+', label: 'Students Placed', icon: <GraduationCap className="w-5 h-5" /> },
    { value: 25, suffix: '+', label: 'Countries', icon: <Globe className="w-5 h-5" /> },
    { value: 94, suffix: '%', label: 'Success Rate', icon: <TrendingUp className="w-5 h-5" /> },
  ];

  return (
    <>
      {showIntro && <LogoIntro onComplete={() => setShowIntro(false)} />}
      {showDemo && <DemoModal onClose={() => setShowDemo(false)} />}
      
      <div className={`min-h-screen transition-opacity duration-500 ${showIntro ? 'opacity-0' : 'opacity-100'}`}>

        {/* Navigation - Dark for logo visibility */}
        <nav className="fixed top-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Admixio" className="h-14" data-testid="logo" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-400 hover:text-white transition-colors text-sm font-medium">Features</a>
              <a href="#partners" className="text-slate-400 hover:text-white transition-colors text-sm font-medium">For Partners</a>
              <a href="#institutions" className="text-slate-400 hover:text-white transition-colors text-sm font-medium">For Institutions</a>
            </div>
            <Link to="/login">
              <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2.5 text-sm font-medium transition-colors" data-testid="get-started-btn">
                Open Portal
              </button>
            </Link>
          </div>
        </nav>

        {/* Hero Section - Dark with blue accent */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-slate-900">
          {/* Subtle blue gradient */}
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-blue-600/8 blur-[120px]" />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-blue-500/6 blur-[100px]" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 py-24 z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
                  <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  <span className="text-sm text-blue-300">Trusted by 500+ recruitment partners worldwide</span>
                </div>
                
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Where Every Student
                  <span className="block text-blue-400">Finds Their Future</span>
                </h1>
                
                <p className="text-lg md:text-xl text-slate-400 mb-8 leading-relaxed max-w-xl">
                  The ultimate B2B platform connecting recruitment partners with world-class universities. Transform dreams into degrees.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/login">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 px-10 text-base font-medium transition-colors flex items-center gap-2" data-testid="hero-cta-btn">
                      Open Portal <ArrowRight className="w-5 h-5" />
                    </button>
                  </Link>
                  <button onClick={() => setShowDemo(true)} className="bg-white/5 hover:bg-white/10 text-white border border-slate-700 rounded-full py-4 px-8 text-base flex items-center gap-2 transition-colors" data-testid="demo-btn">
                    <Play className="w-5 h-5" /> Watch Demo
                  </button>
                </div>
              </div>

              {/* Right - Logo */}
              <div className={`flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                <div className="relative">
                  <div className="absolute inset-0 -m-12">
                    <div className="absolute inset-0 border border-blue-500/10 rounded-full" style={{ animation: 'spin 30s linear infinite' }} />
                    <div className="absolute inset-6 border border-blue-400/8 rounded-full" style={{ animation: 'spin 20s linear infinite reverse' }} />
                  </div>
                  <img src={LOGO_URL} alt="Admixio" className="relative z-10 h-40 md:h-52 lg:h-64" style={{ animation: 'float 4s ease-in-out infinite' }} />
                  <div className="absolute -top-4 -right-4 bg-slate-800 border border-slate-700 px-4 py-2 rounded-full flex items-center gap-2 shadow-xl" style={{ animation: 'fadeIn 1s 0.8s both' }}>
                    <Sparkles className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-white font-medium">AI Powered</span>
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-slate-800 border border-slate-700 px-4 py-2 rounded-full flex items-center gap-2 shadow-xl" style={{ animation: 'fadeIn 1s 1s both' }}>
                    <Shield className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-white font-medium">Secure Portal</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
              <span className="text-xs text-slate-500">Scroll to explore</span>
              <ChevronDown className="w-5 h-5 text-slate-500" />
            </div>
          </div>
        </section>

        {/* Stats Section - Light blue */}
        <section className="py-16 bg-blue-50 border-y border-blue-100 overflow-hidden">
          <div className="marquee-container">
            <div className="marquee-content">
              {[...stats, ...stats].map((stat, index) => (
                <div key={`marquee-${stat.label}-${index}`} className="flex items-center gap-4 px-12">
                  <div className="w-12 h-12 rounded-xl bg-white border border-blue-100 shadow-sm flex items-center justify-center text-blue-600">
                    {stat.icon}
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    </p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section - Light */}
        <section id="features" className="py-24 px-6 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-blue-600 mb-4 block">Features</span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Everything You Need to <span className="text-blue-600">Scale</span>
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto text-lg">
                A comprehensive suite of tools designed for modern recruitment agencies and educational institutions.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={feature.title}
                  className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-5 group-hover:bg-blue-600 group-hover:border-blue-600 transition-colors duration-300">
                    <div className="text-blue-600 group-hover:text-white transition-colors duration-300">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* For Partners Section - White */}
        <section id="partners" className="py-24 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-xs font-bold tracking-[0.3em] uppercase text-blue-600 mb-4 block">For Recruitment Partners</span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Maximize Your <span className="text-blue-600">Recruitment</span> Potential
                </h2>
                <p className="text-slate-600 mb-8 leading-relaxed text-lg">
                  Access our global university network, manage student applications efficiently, and track your commissions in real-time.
                </p>
                <ul className="space-y-4 mb-8">
                  {['Real-time application status tracking', 'AI-powered eligibility assessment', 'Secure document management', 'Transparent commission ledger', 'Dedicated partner support'].map((item) => (
                    <li key={`partner-${item.slice(0,15)}`} className="flex items-center gap-3 text-slate-700">
                      <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/login">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full py-3 px-8 flex items-center gap-2 font-medium transition-colors" data-testid="partner-cta-btn">
                    Become a Partner <ChevronRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                  <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&auto=format&fit=crop&q=60" alt="International Students" className="w-full h-80 object-cover" />
                </div>
                <div className="absolute -bottom-6 -right-6 p-5 rounded-xl bg-white border border-slate-200 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">+47%</p>
                      <p className="text-xs text-slate-500">Conversion Rate</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* For Institutions Section - Slate-50 */}
        <section id="institutions" className="py-24 px-6 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                  <img src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&auto=format&fit=crop&q=60" alt="University Campus" className="w-full h-80 object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 p-5 rounded-xl bg-white border border-slate-200 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">2,500+</p>
                      <p className="text-xs text-slate-500">Applications/Month</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <span className="text-xs font-bold tracking-[0.3em] uppercase text-indigo-600 mb-4 block">For Institutions</span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Streamline Your <span className="text-indigo-600">Admissions</span>
                </h2>
                <p className="text-slate-600 mb-8 leading-relaxed text-lg">
                  Manage applications from a global network of recruitment partners. Issue LOAs, track enrollments, and expand your international student body.
                </p>
                <ul className="space-y-4 mb-8">
                  {['Centralized application management', 'Digital LOA issuance', 'Course and intake management', 'Partner performance analytics', 'Automated compliance checks'].map((item) => (
                    <li key={`inst-${item.slice(0,15)}`} className="flex items-center gap-3 text-slate-700">
                      <div className="w-6 h-6 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={2} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/login">
                  <button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 px-8 flex items-center gap-2 font-medium transition-colors" data-testid="institution-cta-btn">
                    Join as Institution <ChevronRight className="w-5 h-5" />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 bg-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="p-12 rounded-3xl bg-slate-900 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-transparent to-indigo-600/20" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Ready to Transform Your Recruitment?
                </h2>
                <p className="text-slate-400 mb-8 max-w-xl mx-auto text-lg">
                  Join 500+ recruitment partners and institutions already scaling their international education business with Admixio.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/login">
                    <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full py-4 px-10 text-lg flex items-center gap-2 transition-colors" data-testid="final-cta-btn">
                      Get Started Free <ArrowRight className="w-5 h-5" />
                    </button>
                  </Link>
                  <button className="bg-white/10 hover:bg-white/20 text-white border border-slate-600 rounded-full py-4 px-10 text-lg transition-colors">
                    Schedule a Call
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer - Dark for logo */}
        <footer className="py-12 px-6 bg-slate-900 border-t border-slate-800">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <img src={LOGO_URL} alt="Admixio" className="h-10" />
              <p className="text-sm text-slate-500">© 2026 Admixio. All rights reserved.</p>
              <div className="flex items-center gap-6">
                <a href="#" className="text-sm text-slate-500 hover:text-slate-300 transition-colors">Privacy</a>
                <a href="#" className="text-sm text-slate-500 hover:text-slate-300 transition-colors">Terms</a>
                <Link to="/contact" className="text-sm text-slate-500 hover:text-slate-300 transition-colors">Contact</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default LandingPage;
